import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../../config/supabase';

interface TeamMember {
  id: number;
  name: string;
  position: string;
  bio: string;
  image_url: string;
  display_order: number;
  is_active: boolean;
}

const Team = () => {
  const [animationKey, setAnimationKey] = useState(0);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimationKey(prev => prev + 1);
          }
        });
      },
      { threshold: 0.3 }
    );

    const section = document.getElementById('team');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setTeamMembers(data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="team" className="pt-8 sm:pt-12 lg:pt-16 pb-14 sm:pb-20 lg:pb-24 bg-cream">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
        <motion.div
          key={`team-header-${animationKey}`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-10 lg:mb-12"
        >
          <div className="text-navy text-xs sm:text-sm font-serif tracking-wider mb-2 sm:mb-3">OUR TEAM</div>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 px-4">
            <span className="text-navy">Expertise You Can </span>
            <span className="text-gold italic">Trust</span>
          </h2>
          <p className="text-text-light text-xs sm:text-sm lg:text-base max-w-3xl mx-auto leading-relaxed px-4">
            Our distinguished team combines decades of experience in finance, property, and healthcare, united by a commitment to preserving and growing family wealth.
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center py-16">
            <div className="text-navy text-base sm:text-lg">Loading team members...</div>
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-navy text-base sm:text-lg">No team members available</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {teamMembers.map((member, index) => (
              <motion.div
                key={`${member.id}-${animationKey}`}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-2xl mb-3 sm:mb-4 h-72 sm:h-80 lg:h-[22rem]">
                  <img
                    src={member.image_url}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <h3 className="text-navy text-lg sm:text-xl font-bold mb-1 break-words">{member.name}</h3>
                <div className="text-gold text-xs font-semibold mb-2 break-words">{member.position}</div>
                <p className="text-text-light text-xs leading-relaxed">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Team Values */}
        <motion.div
          key={`team-values-${animationKey}`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-10 sm:mt-14 lg:mt-16 bg-white rounded-2xl p-6 sm:p-8 lg:p-10 shadow-xl"
        >
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-navy mb-2 sm:mb-3 px-4">Our Core Values</h3>
            <p className="text-text-light text-xs sm:text-sm max-w-2xl mx-auto px-4">
              The principles that guide every decision we make on behalf of our clients.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="text-center group cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-gold/40 group-hover:scale-110 transition-all duration-300">
                <i className="ri-shield-star-line text-gold text-lg sm:text-xl group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <h4 className="text-navy font-bold mb-1 text-xs sm:text-sm group-hover:text-gold transition-colors duration-300">Integrity</h4>
              <p className="text-text-light text-xs">Unwavering ethical standards</p>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-gold/40 group-hover:scale-110 transition-all duration-300">
                <i className="ri-lightbulb-line text-gold text-lg sm:text-xl group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <h4 className="text-navy font-bold mb-1 text-xs sm:text-sm group-hover:text-gold transition-colors duration-300">Innovation</h4>
              <p className="text-text-light text-xs">Forward-thinking strategies</p>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-gold/40 group-hover:scale-110 transition-all duration-300">
                <i className="ri-user-heart-line text-gold text-lg sm:text-xl group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <h4 className="text-navy font-bold mb-1 text-xs sm:text-sm group-hover:text-gold transition-colors duration-300">Dedication</h4>
              <p className="text-text-light text-xs">Committed to your success</p>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-gold/40 group-hover:scale-110 transition-all duration-300">
                <i className="ri-award-line text-gold text-lg sm:text-xl group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <h4 className="text-navy font-bold mb-1 text-xs sm:text-sm group-hover:text-gold transition-colors duration-300">Excellence</h4>
              <p className="text-text-light text-xs">Exceptional in every detail</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Team;
